import { useState } from 'react';
import { Button } from '@mui/material';
import {
  Confirm,
  useDataProvider,
  useNotify,
  useRefresh,
  type Identifier,
} from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { performStatusTransition } from '../actions/performStatusTransition';
import { ERASED_MARKER } from '../dataProvider/types';

/**
 * The IRREVERSIBLE erasure execute (D-erasure, Phase 7 surface 6). The sharpest action in the build:
 * gated BOTH on the grace window having ELAPSED (the surface only renders this when `row.executable`)
 * AND on `erasure:execute`, which is SUPER_ADMIN-only (the maker-checker: compliance proposes,
 * super_admin executes).
 *
 * Execute is a RECORDED DECISION plus a MINIMAL OWNED-ROW TOMBSTONE, never a real cascade delete (the
 * mock provider cannot cascade and there is no deletion engine). On confirm it:
 *   1. Records the decision through the chokepoint (erasureRequests -> executed), capability-gated
 *      and audited. This is the single gate: a non-super_admin call throws here, before any scrub.
 *   2. Tombstones the User row: scrubs the identifying PII to the erased marker and severs the
 *      referral link, KEEPING the row and its id so retained records resolve to a tombstone.
 *   3. Severs the member's transactions (userId -> null), the only other nullable link.
 * Everything else (the hard-FK anonymise-retain records and the click delete) is the BACKEND seam;
 * the mock does NOT touch, widen, or delete them.
 */
export const ExecuteErasureAction = ({
  id,
  subjectUserId,
  now,
}: {
  id: Identifier;
  subjectUserId: number;
  now: number;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const onConfirm = async () => {
    setLoading(true);
    try {
      const iso = new Date(now).toISOString();

      // 1. Record the decision (capability-gated + audited). Throws if not erasure:execute, so this
      // is the single enforcement point before any data is touched.
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'erasure:execute',
        resource: 'erasureRequests',
        id,
        data: { status: 'executed', executedAt: iso },
        previousData: { id, status: 'requested', executedAt: null },
      });

      // 2. The minimal owned-row TOMBSTONE: scrub the User PII, sever the referral link, keep the row.
      const user = (await dataProvider.getOne('users', { id: subjectUserId }))
        .data;
      await dataProvider.update('users', {
        id: subjectUserId,
        data: {
          name: ERASED_MARKER,
          email: ERASED_MARKER,
          signupAddress: null,
          ipAddress: null,
          deviceId: null,
          billingReference: null,
          legacyUserId: null,
          referredBy: null,
        },
        previousData: user,
      });

      // 3. Sever the member's transactions (the only other nullable link). The financial records are
      // kept; the person is removed. The retained hard-FK records are the backend seam, untouched here.
      const txns = (
        await dataProvider.getList('transactions', {
          pagination: { page: 1, perPage: 100_000 },
          sort: { field: 'id', order: 'ASC' },
          filter: { userId: subjectUserId },
        })
      ).data;
      if (txns.length > 0) {
        await dataProvider.updateMany('transactions', {
          ids: txns.map((t) => t.id),
          data: { userId: null },
        });
      }

      notify('Erasure executed', { type: 'success' });
      setOpen(false);
      setLoading(false);
      refresh();
    } catch {
      notify('Action not permitted or failed', { type: 'error' });
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="error"
        onClick={() => setOpen(true)}
        data-testid={`erasure-execute-${id}`}
      >
        Execute erasure
      </Button>
      <Confirm
        isOpen={open}
        loading={loading}
        title="Execute this erasure"
        content="This is irreversible. The member profile is tombstoned and the cascade runs downstream. There is no undo."
        confirm="Execute"
        cancel="Cancel"
        onConfirm={onConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
